import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import CartDrawer from "../Cart/CartDrawer";
import { useCart } from "../Cart/CartContext";
import Swal from "sweetalert2";
import "./PurchasePanel.css";
import api from "../../api";

export default function PurchasePanel({
  product,
  categories = [],
  currency = "$",
}) {
  const navigate = useNavigate();
  const { cartItems, addToCart, removeItem, increaseQty, decreaseQty } =
    useCart();

  const [cartOpen, setCartOpen] = useState(false);

  // Ip Test
  const [isLebanon, setIsLebanon] = useState(null);
  const [loadingLebanon, setLoadingLebanon] = useState(true);

  const inStock = Number(product?.inStock) === 1;
  const maxQty = Math.max(1, Number(product?.qty) || 1);
  const [qty, setQty] = useState(inStock ? 1 : 0);

  const priceText = useMemo(() => {
    const n = Number(product?.price) || 0;
    return `${currency}${n.toFixed(2)}`;
  }, [product?.price, currency]);

  const clean = (value) => (typeof value === "string" ? value.trim() : "");

  const artistEn = clean(product?.artistEn);
  const titleEn = clean(product?.titleEn);
  const artistAr = clean(product?.artistAr);
  const titleAr = clean(product?.titleAr);

  const hasArtistAr = artistAr && artistAr !== artistEn;
  const hasTitleAr = titleAr && titleAr !== titleEn;
  const hasArabic = hasArtistAr || hasTitleAr;

  const infoItems = categories.map((cat) => cat?.catDescr).filter(Boolean);

  const dec = () => setQty((q) => Math.max(1, q - 1));
  const inc = () => setQty((q) => Math.min(maxQty, q + 1));

  const existing = cartItems.find((x) => x.id === product.id);

  const handleAddToCart = () => {
   if (!inStock || qty < 1) return;

  const total = (Number(existing?.qty) || 0) + (Number(qty) || 0);

  if (total > product.qty) {
    Swal.fire({
      icon: "warning",
      title: "Out of Stock",
      text: `Only ${product.qty} available for this product.`,
      confirmButtonColor: "#000",
      customClass: {
        popup: "my-swal-popup",
      },
    });
    return;
  }
    addToCart(product, qty);
    setCartOpen(true);
  };

  const checkIfLebanon = async () => {
    try {
      setLoadingLebanon(true);

      const res = await api.post("user_ip/check-lebanon", {
        // clientIp: "188.170.72.89"
      });

      setIsLebanon(res.data?.isLebanon === true);
    } catch (err) {
      console.error("Lebanon check error:", err);
      setIsLebanon(false);
    } finally {
      setLoadingLebanon(false);
    }
  };

  useEffect(() => {
    checkIfLebanon();
  }, [product]);

  return (
    <aside className="ppp-card" aria-label="Purchase panel">
      <div className={`ppp-top ${hasArabic ? "" : "is-single"}`}>
        <div className="ppp-nameCol ppp-nameCol--en">
          {!!artistEn && <div className="ppp-nameArtist">{artistEn}</div>}
          {!!titleEn && <div className="ppp-nameTitle">{titleEn}</div>}
        </div>

        {hasArabic && (
          <div className="ppp-nameCol ppp-nameCol--ar" dir="rtl">
            {hasArtistAr && <div className="ppp-nameArtist">{artistAr}</div>}
            {hasTitleAr && <div className="ppp-nameTitle">{titleAr}</div>}
          </div>
        )}
      </div>

      <div className="ppp-bottom">
        <div className="ppp-bottomLeft">
          {!!infoItems.length && (
            <div className="ppp-metaInfo">
              {infoItems.map((item, index) => (
                <React.Fragment key={`${item}-${index}`}>
                  {index > 0 && <span className="ppp-dot">•</span>}
                  {/* <span>{item}</span> */}
                  <span className="ppp-metaText">{item}</span>
                </React.Fragment>
              ))}
            </div>
          )}

          <div className="ppp-metaMain">
            {!!product?.format && (
              <span className="ppp-chip">
                <b>{product?.nbofdiscs ? `${product.nbofdiscs} ` : ""}</b>
                {/* {product.format} */}
                <span className="ppp-chipText">{product.format}</span>
              </span>
            )}

            {/* {!!product?.remarks && (
              <span className="ppp-chip">
                <b className="ppp-chipText">{product.remarks}</b>
              </span>
            )} */}
            {product?.remarks
              ?.split(",")
              .map((item) => item.trim())
              .filter(Boolean)
              .map((remark, index) => (
                <span className="ppp-chip" key={index}>
                  <b className="ppp-chipText">{remark}</b>
                </span>
              ))}
          </div>
        </div>

        <div className="ppp-bottomRight">
          <div className="ppp-priceRow">
            <div className="ppp-price">{priceText}</div>

            <div className={`ppp-stock ${inStock ? "is-in" : "is-out"}`}>
              <span className="ppp-stockDot" aria-hidden="true" />
              {inStock ? "In Stock" : "Out of Stock"}
            </div>
          </div>

          <div className="ppp-actionsRow">
            <div
              className="ppp-qty"
              role="group"
              aria-label="Quantity selector"
            >
              <button
                type="button"
                className="ppp-qtyBtn"
                onClick={dec}
                disabled={!inStock || qty <= 1}
                aria-label="Decrease quantity"
              >
                −
              </button>

              <div
                className={`ppp-qtyVal ${qty === 0 ? "is-disabled" : ""}`}
                aria-live="polite"
              >
                {qty}
              </div>

              <button
                type="button"
                className="ppp-qtyBtn"
                onClick={inc}
                disabled={!inStock || qty >= maxQty}
                aria-label="Increase quantity"
              >
                +
              </button>
            </div>

            <button
              type="button"
              className="ppp-add"
              onClick={handleAddToCart}
              disabled={!inStock}
            >
              ADD TO CART
            </button>
          </div>

          {isLebanon && (
            <div className="ppp-shipping">Delivery within 5 working days</div>
          )}
        </div>
      </div>

      <CartDrawer
        isOpen={cartOpen}
        onClose={() => setCartOpen(false)}
        items={cartItems}
        onRemoveItem={removeItem}
        onIncreaseQty={increaseQty}
        onDecreaseQty={decreaseQty}
        onViewCart={() => navigate("/cart")}
        onCheckout={() => navigate("/CheckOut")}
        onContinueShopping={() => setCartOpen(false)}
      />
    </aside>
  );
}
